import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, Box, Button, InputAdornment, TextField } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, type ReactElement, useDeferredValue, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RosterLoadFallback } from "@/components/RosterLoadFallback/RosterLoadFallback";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useRequestsContext } from "@/contexts/RequestsContext";
import { useWeekViewContext } from "@/contexts/WeekView/WeekViewContext";
import { useDepartmentCallInsForDay } from "@/hooks/useCallIns";
import { useRosterLoadState } from "@/hooks/useRosterLookup";
import { FLUID_FONT_SIZE, INK, NEUTRAL, SURFACE } from "@/theme/tokens";
import { getShiftDisplayDate } from "@/utils/dateUtils";
import type { NurseInShift, OpenShiftRequest, ReplaceShiftRequest } from "@/utils/models";
import { toIsoDate } from "@/utils/scheduleWeeks";
import { MobileCallInsList } from "./CallInsList/MobileCallInsList";
import { MobileNursesList } from "./NursesList/MobileNursesList";
import { PostedShiftsList } from "./PostShift/PostedShiftList";

type MobileStaffingContentProps = { date: DateTime };

enum StaffingContentMode {
	Nurses = "Nurses",
	PostedShifts = "Posted Shifts",
	CallIns = "Call-Ins",
}

// The enum values double as stable analytics/test identifiers, so the human-facing pill labels are
// looked up separately instead of rendering the raw value (which was never translated).
const MODE_LABEL_KEYS: Record<StaffingContentMode, string> = {
	[StaffingContentMode.Nurses]: "nurses",
	[StaffingContentMode.PostedShifts]: "posted.shifts",
	[StaffingContentMode.CallIns]: "call.ins",
};

export const MobileStaffingContent: FC<MobileStaffingContentProps> = ({ date }) => {
	const { staffRequests } = useRequestsContext();
	const { chosenDepartment, isMedicalDepartment } = useHospitalData();
	const { weekViewData } = useWeekViewContext();
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const [mode, setMode] = useState<StaffingContentMode>(StaffingContentMode.Nurses);
	const [searchTerm, setSearchTerm] = useState<string>("");

	const requests: OpenShiftRequest[] | ReplaceShiftRequest[] = useMemo(
		() =>
			staffRequests.filter(
				(request): request is OpenShiftRequest =>
					"needed_nurses" in request &&
					getShiftDisplayDate(request.message.shift_start, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).toFormat("yyyy-MM-dd") ===
						date.toFormat("yyyy-MM-dd"),
			),
		[staffRequests, date, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	const nurses: NurseInShift[] = useMemo(() => {
		// The shown week's day item holds every shift row's shifts on the date.
		const dayItem = weekViewData.find((day) => toIsoDate(day.date) === toIsoDate(date));
		if (!dayItem) return [];

		return dayItem.shifts
			.filter((nurse: NurseInShift) => !nurse.isPendingToDelete)
			.sort((a: NurseInShift, b: NurseInShift) => {
				if (a.charge && !b.charge) return -1;
				if (!a.charge && b.charge) return 1;

				const timeCompare: number = a.startTime.diff(b.startTime).milliseconds;

				if (timeCompare !== 0) return timeCompare;

				const roleCompare: number = b.role.toLowerCase().localeCompare(a.role.toLowerCase());

				if (roleCompare !== 0) return roleCompare;

				return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
			});
	}, [weekViewData, date]);

	const deferredSearchTerm = useDeferredValue(searchTerm);

	const filteredNurses: NurseInShift[] = useMemo(() => {
		const query = deferredSearchTerm.trim().toLowerCase();
		if (!query) return nurses;
		return nurses.filter((nurse) => nurse.name.toLowerCase().includes(query));
	}, [nurses, deferredSearchTerm]);

	const nurseNameOptions: string[] = useMemo(() => Array.from(new Set(nurses.map((n) => n.name))), [nurses]);

	// Fetched whether or not the tab is open: the two schedule modes read their counts off already-loaded
	// context data, and an unopened "Call-Ins 3" pill is the whole point of surfacing the section here.
	// Skipped for medical departments: unlike desktop (`StaffingWrapper` routes them to `SynergyModeStaffing`
	// before ever reaching this grid), the mobile staffing route has no such branch, so this component has
	// to gate itself directly — matching the profile calendar (`VirtualizedWeekScheduleGrid`) and
	// `ScheduleSection`, which hide call-ins entirely for the same departments.
	const { callIns, isLoading: isLoadingCallIns, isError: isCallInsError } = useDepartmentCallInsForDay({ date, enabled: !isMedicalDepartment });
	// The posted shifts and call-ins name people who are often not in the week, from the staff list; the staff tab
	// reads the week's own people.
	const rosterLoad = useRosterLoadState(chosenDepartment.department_id);
	const isRosterPending = rosterLoad.isLoading || rosterLoad.isError;

	const visibleModes = useMemo(
		() => Object.values(StaffingContentMode).filter((contentMode) => contentMode !== StaffingContentMode.CallIns || !isMedicalDepartment),
		[isMedicalDepartment],
	);

	const activeView = useMemo((): ReactElement => {
		switch (mode) {
			case StaffingContentMode.Nurses:
				return <MobileNursesList nurses={filteredNurses} />;
			case StaffingContentMode.PostedShifts:
				return isRosterPending ? <RosterLoadFallback roster={rosterLoad} dataTestId="mobile-staffing-posted" /> : <PostedShiftsList requests={requests} />;
			case StaffingContentMode.CallIns:
				return isRosterPending ? (
					<RosterLoadFallback roster={rosterLoad} dataTestId="mobile-staffing-call-ins" />
				) : (
					<MobileCallInsList callIns={callIns} isLoading={isLoadingCallIns} isError={isCallInsError} />
				);
		}
	}, [mode, filteredNurses, requests, callIns, isLoadingCallIns, isCallInsError, isRosterPending, rosterLoad]);

	const counters = {
		[StaffingContentMode.Nurses]: nurses.length,
		[StaffingContentMode.PostedShifts]: requests.length,
		[StaffingContentMode.CallIns]: callIns.length,
	};

	return (
		<Box sx={{ width: "100%", px: 2 }}>
			{mode === StaffingContentMode.Nurses && (
				<Box sx={{ mb: 2 }}>
					<Autocomplete
						freeSolo
						autoSelect
						options={nurseNameOptions}
						inputValue={searchTerm}
						onInputChange={(_, value) => setSearchTerm(value)}
						onChange={(_, value) => {
							const trimmed = (value ?? "").trim();
							if (trimmed) track("mobile_staffing_search", { search_term: trimmed });
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder={t("search.staff")}
								size="small"
								data-testid="mobile-staffing-search-input"
								slotProps={{
									...params.slotProps,
									input: {
										...params.slotProps.input,
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon sx={{ color: INK.SECONDARY }} />
											</InputAdornment>
										),
										sx: { bgcolor: SURFACE, borderRadius: 1 },
									},
								}}
							/>
						)}
					/>
				</Box>
			)}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-evenly",
					// Wrapping is the escape valve the fluid font can't cover on its own: Spanish
					// ("Turnos publicados") is far wider than English at the same size, so on a 320px
					// screen the row drops a pill to a second line instead of clipping the labels.
					flexWrap: "wrap",
					gap: 0.5,
					marginBottom: 2,
				}}
			>
				{visibleModes.map((contentMode: StaffingContentMode) => (
					<Button
						key={contentMode}
						data-testid={`mobile-staffing-mode-${contentMode.toLowerCase().replace(/[^a-z]+/g, "-")}`}
						onClick={() => {
							track("mobile_staffing_mode_switch", { mode: contentMode, date: date.toFormat("yyyy-MM-dd") });
							setMode(contentMode);
						}}
						sx={{
							color: mode === contentMode ? "white" : "primary.main",
							backgroundColor: mode === contentMode ? "primary.main" : "transparent",
							borderRadius: "100px",
							height: "25px",
							// Three uppercase labels plus their counts have to share the width of the
							// narrowest phone. The font scales with the viewport (see the token) and the
							// horizontal padding tightens with it, since the theme's 20px Button padding
							// alone eats 120px of a 320px screen across three pills.
							fontSize: FLUID_FONT_SIZE.PILL_LABEL,
							px: { xs: 1, sm: 2.5 },
							// MUI floors a Button at 64px wide and refuses to shrink past its content;
							// both have to go or the labels win and the row overflows.
							minWidth: 0,
							// A label that wraps would break out of the fixed 25px pill height.
							whiteSpace: "nowrap",
							transition: "all 0.3s",
							"&:hover": {
								backgroundColor: mode === contentMode ? "primary.dark" : NEUTRAL[100],
							},
						}}
					>
						{t(MODE_LABEL_KEYS[contentMode])} {counters[contentMode]}
					</Button>
				))}
			</Box>
			{activeView}
		</Box>
	);
};
